/**
 * Per-locale geo targeting map.
 *
 * SEO-only signalling: each language is served on a single URL (e.g. `/es/...`),
 * but we advertise it to every country that officially speaks that language via
 * region-coded hreflang alternates (`es-MX`, `es-AR`, ...) and by mentioning
 * those countries in the meta description, keywords, and JSON-LD `audience`.
 *
 * Country list sourced from the uploaded Language.pdf (official-language table)
 * and extended for locales not covered by the PDF (el, sv, fil, am).
 */

export interface GeoCountry {
  /** ISO-3166-1 alpha-2, uppercase (e.g. "MX"). */
  iso: string;
  /** English display name used in description enrichment + keywords. */
  name: string;
  /** Optional Facebook `og:locale:alternate` value if a canonical FB tag exists. */
  fbLocale?: string;
}

/**
 * Enabled locales → list of target countries. Order matters: the first few
 * entries are used when appending country mentions to the meta description,
 * so keep the highest-traffic markets first.
 */
export const GEO_TARGETS: Record<string, GeoCountry[]> = {
  en: [
    { iso: "US", name: "United States", fbLocale: "en_US" },
    { iso: "GB", name: "United Kingdom", fbLocale: "en_GB" },
    { iso: "CA", name: "Canada", fbLocale: "en_CA" },
    // North American English-serving markets and territories. Canada sits in
    // the top region-tag slots; the rest carry description/keyword and JSON-LD
    // audience signals so US/Canada/Mexico + Caribbean coverage is explicit.
    { iso: "MX", name: "Mexico" },
    { iso: "PR", name: "Puerto Rico" },
    { iso: "BM", name: "Bermuda" },
    { iso: "KY", name: "Cayman Islands" },
    { iso: "VI", name: "U.S. Virgin Islands" },
    { iso: "VG", name: "British Virgin Islands" },
    { iso: "TC", name: "Turks and Caicos Islands" },
    { iso: "AI", name: "Anguilla" },
    { iso: "MS", name: "Montserrat" },
    { iso: "GL", name: "Greenland" },

    { iso: "AU", name: "Australia", fbLocale: "en_AU" },
    { iso: "IN", name: "India", fbLocale: "en_IN" },
    { iso: "NG", name: "Nigeria" },
    { iso: "ZA", name: "South Africa" },
    { iso: "IE", name: "Ireland" },
    { iso: "NZ", name: "New Zealand" },
    { iso: "KE", name: "Kenya" },
    { iso: "GH", name: "Ghana" },
    { iso: "SG", name: "Singapore" },
    { iso: "PH", name: "Philippines" },
    { iso: "PK", name: "Pakistan" },
    { iso: "JM", name: "Jamaica" },
    { iso: "TT", name: "Trinidad and Tobago" },
    { iso: "UG", name: "Uganda" },
    { iso: "TZ", name: "Tanzania" },
    { iso: "ZM", name: "Zambia" },
    { iso: "ZW", name: "Zimbabwe" },
    { iso: "MW", name: "Malawi" },
    { iso: "BW", name: "Botswana" },
    { iso: "NA", name: "Namibia" },
    { iso: "SL", name: "Sierra Leone" },
    { iso: "LR", name: "Liberia" },
    { iso: "RW", name: "Rwanda" },
    { iso: "CM", name: "Cameroon" },
    { iso: "MT", name: "Malta" },
    { iso: "MU", name: "Mauritius" },
    { iso: "FJ", name: "Fiji" },
    { iso: "PG", name: "Papua New Guinea" },
    { iso: "SS", name: "South Sudan" },
    { iso: "SD", name: "Sudan" },
    { iso: "BS", name: "Bahamas" },
    { iso: "BB", name: "Barbados" },
    { iso: "BZ", name: "Belize" },
    { iso: "GY", name: "Guyana" },
    { iso: "AG", name: "Antigua and Barbuda" },
    { iso: "DM", name: "Dominica" },
    { iso: "GD", name: "Grenada" },
    { iso: "KN", name: "Saint Kitts and Nevis" },
    { iso: "LC", name: "Saint Lucia" },
    { iso: "VC", name: "Saint Vincent and the Grenadines" },
    { iso: "LS", name: "Lesotho" },
    { iso: "SZ", name: "Eswatini" },
    { iso: "SC", name: "Seychelles" },
    { iso: "GM", name: "Gambia" },
    { iso: "SB", name: "Solomon Islands" },
    { iso: "VU", name: "Vanuatu" },
    { iso: "WS", name: "Samoa" },
    { iso: "TO", name: "Tonga" },
    { iso: "KI", name: "Kiribati" },
    { iso: "TV", name: "Tuvalu" },
    { iso: "NR", name: "Nauru" },
    { iso: "MH", name: "Marshall Islands" },
    { iso: "FM", name: "Micronesia" },
    { iso: "PW", name: "Palau" },
  ],
  es: [
    { iso: "ES", name: "Spain", fbLocale: "es_ES" },
    { iso: "MX", name: "Mexico", fbLocale: "es_MX" },
    { iso: "AR", name: "Argentina", fbLocale: "es_LA" },
    { iso: "CO", name: "Colombia", fbLocale: "es_CO" },
    { iso: "CL", name: "Chile", fbLocale: "es_CL" },
    // North American Spanish markets: the US Hispanic audience and Puerto Rico.
    // Kept out of the top three so English keeps the `en-US` primary claim.
    { iso: "US", name: "United States" },
    { iso: "PR", name: "Puerto Rico" },

    { iso: "PE", name: "Peru" },
    { iso: "VE", name: "Venezuela", fbLocale: "es_VE" },
    { iso: "EC", name: "Ecuador" },
    { iso: "GT", name: "Guatemala" },
    { iso: "CU", name: "Cuba" },
    { iso: "BO", name: "Bolivia" },
    { iso: "DO", name: "Dominican Republic" },
    { iso: "HN", name: "Honduras" },
    { iso: "PY", name: "Paraguay" },
    { iso: "SV", name: "El Salvador" },
    { iso: "NI", name: "Nicaragua" },
    { iso: "CR", name: "Costa Rica" },
    { iso: "PA", name: "Panama" },
    { iso: "UY", name: "Uruguay" },
    { iso: "GQ", name: "Equatorial Guinea" },
  ],
  pt: [
    { iso: "BR", name: "Brazil", fbLocale: "pt_BR" },
    { iso: "PT", name: "Portugal", fbLocale: "pt_PT" },
    { iso: "AO", name: "Angola" },
    { iso: "MZ", name: "Mozambique" },
    { iso: "CV", name: "Cabo Verde" },
    { iso: "GW", name: "Guinea-Bissau" },
    { iso: "TL", name: "Timor-Leste" },
    { iso: "ST", name: "São Tomé and Príncipe" },
    { iso: "GQ", name: "Equatorial Guinea" },
  ],
  fr: [
    { iso: "FR", name: "France", fbLocale: "fr_FR" },
    { iso: "CA", name: "Canada", fbLocale: "fr_CA" },
    // French-speaking North America / Caribbean.
    { iso: "PM", name: "Saint Pierre and Miquelon" },
    { iso: "GP", name: "Guadeloupe" },
    { iso: "MQ", name: "Martinique" },
    { iso: "BL", name: "Saint Barthélemy" },
    { iso: "MF", name: "Saint Martin" },

    { iso: "BE", name: "Belgium" },
    { iso: "CH", name: "Switzerland" },
    { iso: "LU", name: "Luxembourg" },
    { iso: "MC", name: "Monaco" },
    { iso: "SN", name: "Senegal" },
    { iso: "CI", name: "Ivory Coast" },
    { iso: "CM", name: "Cameroon" },
    { iso: "MG", name: "Madagascar" },
    { iso: "CD", name: "DR Congo" },
    { iso: "CG", name: "Republic of the Congo" },
    { iso: "GA", name: "Gabon" },
    { iso: "ML", name: "Mali" },
    { iso: "BF", name: "Burkina Faso" },
    { iso: "NE", name: "Niger" },
    { iso: "TD", name: "Chad" },
    { iso: "GN", name: "Guinea" },
    { iso: "BJ", name: "Benin" },
    { iso: "TG", name: "Togo" },
    { iso: "BI", name: "Burundi" },
    { iso: "RW", name: "Rwanda" },
    { iso: "DJ", name: "Djibouti" },
    { iso: "KM", name: "Comoros" },
    { iso: "CF", name: "Central African Republic" },
    { iso: "HT", name: "Haiti" },
    { iso: "SC", name: "Seychelles" },
    { iso: "VU", name: "Vanuatu" },
    { iso: "GQ", name: "Equatorial Guinea" },
  ],
  de: [
    { iso: "DE", name: "Germany", fbLocale: "de_DE" },
    { iso: "AT", name: "Austria", fbLocale: "de_AT" },
    { iso: "CH", name: "Switzerland", fbLocale: "de_CH" },
    { iso: "LU", name: "Luxembourg" },
    { iso: "LI", name: "Liechtenstein" },
    { iso: "BE", name: "Belgium" },
  ],
  it: [
    { iso: "IT", name: "Italy", fbLocale: "it_IT" },
    { iso: "CH", name: "Switzerland" },
    { iso: "SM", name: "San Marino" },
    { iso: "VA", name: "Vatican City" },
  ],
  ar: [
    { iso: "SA", name: "Saudi Arabia", fbLocale: "ar_AR" },
    { iso: "AE", name: "United Arab Emirates" },
    { iso: "EG", name: "Egypt" },
    { iso: "MA", name: "Morocco" },
    { iso: "DZ", name: "Algeria" },
    { iso: "IQ", name: "Iraq" },
    { iso: "TN", name: "Tunisia" },
    { iso: "JO", name: "Jordan" },
    { iso: "LB", name: "Lebanon" },
    { iso: "KW", name: "Kuwait" },
    { iso: "QA", name: "Qatar" },
    { iso: "BH", name: "Bahrain" },
    { iso: "OM", name: "Oman" },
    { iso: "YE", name: "Yemen" },
    { iso: "SY", name: "Syria" },
    { iso: "LY", name: "Libya" },
    { iso: "SD", name: "Sudan" },
    { iso: "PS", name: "Palestine" },
    { iso: "MR", name: "Mauritania" },
    { iso: "SO", name: "Somalia" },
    { iso: "DJ", name: "Djibouti" },
    { iso: "KM", name: "Comoros" },
    { iso: "TD", name: "Chad" },
  ],
  ru: [
    { iso: "RU", name: "Russia", fbLocale: "ru_RU" },
    { iso: "BY", name: "Belarus" },
    { iso: "KZ", name: "Kazakhstan" },
    { iso: "KG", name: "Kyrgyzstan" },
  ],
  ja: [{ iso: "JP", name: "Japan", fbLocale: "ja_JP" }],
  ko: [
    { iso: "KR", name: "South Korea", fbLocale: "ko_KR" },
    { iso: "KP", name: "North Korea" },
  ],
  hi: [
    { iso: "IN", name: "India", fbLocale: "hi_IN" },
    { iso: "FJ", name: "Fiji" },
  ],
  tr: [
    { iso: "TR", name: "Turkey", fbLocale: "tr_TR" },
    { iso: "CY", name: "Cyprus" },
  ],
  id: [{ iso: "ID", name: "Indonesia", fbLocale: "id_ID" }],
  nl: [
    { iso: "NL", name: "Netherlands", fbLocale: "nl_NL" },
    { iso: "BE", name: "Belgium", fbLocale: "nl_BE" },
    { iso: "SR", name: "Suriname" },
  ],
  pl: [{ iso: "PL", name: "Poland", fbLocale: "pl_PL" }],
  el: [
    { iso: "GR", name: "Greece", fbLocale: "el_GR" },
    { iso: "CY", name: "Cyprus" },
  ],
  sv: [
    { iso: "SE", name: "Sweden", fbLocale: "sv_SE" },
    { iso: "FI", name: "Finland" },
  ],
  fil: [{ iso: "PH", name: "Philippines", fbLocale: "fil_PH" }],
  am: [{ iso: "ET", name: "Ethiopia" }],
  zh: [
    { iso: "CN", name: "China", fbLocale: "zh_CN" },
    { iso: "SG", name: "Singapore" },
  ],
  "zh-TW": [
    { iso: "TW", name: "Taiwan", fbLocale: "zh_TW" },
    { iso: "HK", name: "Hong Kong", fbLocale: "zh_HK" },
  ],
  vi: [{ iso: "VN", name: "Vietnam", fbLocale: "vi_VN" }],
  ms: [
    { iso: "MY", name: "Malaysia", fbLocale: "ms_MY" },
    { iso: "BN", name: "Brunei" },
    { iso: "SG", name: "Singapore" },
  ],
  th: [{ iso: "TH", name: "Thailand", fbLocale: "th_TH" }],
};


/** Every country entry for a locale (empty array if the locale is unknown). */
export function countriesForLocale(code: string): GeoCountry[] {
  return GEO_TARGETS[code] ?? [];
}

/**
 * Fallback cap on region-coded hreflang alternates for locales without an
 * explicit list below.
 *
 * Google treats every `xx-YY` tag as a separate targeting claim, so emitting a
 * tag for all ~57 English-speaking territories produced a ~194-tag cluster on
 * every page — the "conflicting hreflang" and bloat findings in the audit. We
 * cap the cluster to the highest-traffic markets per language.
 */
const MAX_REGION_TAGS = 5;

/**
 * Explicit, ordered region-hreflang markets per locale.
 *
 * Relying on "first N countries in GEO_TARGETS" silently dropped Statlign's core
 * African English markets (Nigeria, South Africa, Kenya, Ghana) behind Caribbean
 * territories, so they received no `en-XX` targeting signal at all while Canada
 * did. Listing the markets explicitly keeps Canada, North America, Africa, Asia
 * and Oceania all represented, and keeps the cluster deliberate rather than
 * an accident of array order.
 *
 * Every ISO here must exist in that locale's `GEO_TARGETS` list. A country MAY
 * appear under more than one locale (`en-CA` and `fr-CA`, `de-CH` and `fr-CH`)
 * — that is a language+region pair, not a conflict, and it is exactly how
 * bilingual markets should be signalled. The emitted tag itself is unique.
 */
const REGION_HREFLANG: Record<string, string[]> = {
  // North America + core African English markets + Asia-Pacific.
  en: ["US", "GB", "CA", "NG", "ZA", "KE", "GH", "AU", "IN", "IE", "NZ"],
  // France + francophone Canada + francophone West/Central Africa.
  fr: ["FR", "CA", "BE", "CH", "SN", "CI", "CM", "CD"],
  // Iberia + Latin America.
  es: ["ES", "MX", "AR", "CO", "CL", "PE"],
  // Brazil, Portugal + lusophone Africa.
  pt: ["BR", "PT", "AO", "MZ"],
  // Gulf + North Africa.
  ar: ["SA", "AE", "EG", "MA", "DZ"],
  de: ["DE", "AT", "CH"],
  it: ["IT", "CH"],
  hi: ["IN"],
  am: ["ET"],
};

/**
 * Countries where a *different* supported language is the dominant market
 * (i.e. it sits in that language's top 3). Used only for locales without an
 * explicit list above, so an unlisted language never claims a territory that
 * already belongs to another language's primary market.
 */
const DOMINANT_BY_COUNTRY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [code, list] of Object.entries(GEO_TARGETS)) {
    for (const c of list.slice(0, 3)) {
      if (!map[c.iso]) map[c.iso] = code;
    }
  }
  return map;
})();

/**
 * Region-coded hreflang tags for a locale, in the form `xx-YY` (ISO 639-1
 * language + ISO 3166-1 alpha-2 region). Script variants such as `zh-TW` keep
 * their ISO 15924-derived base and are not double-suffixed.
 *
 * Example: `hreflangRegionTags("es")` → `["es-ES","es-MX","es-AR","es-CO","es-CL","es-PE"]`.
 */
export function hreflangRegionTags(code: string): string[] {
  // `zh-TW` is already a fully-qualified tag; adding a region would be invalid.
  if (code.includes("-")) return [];
  const lang = code.toLowerCase();

  const explicit = REGION_HREFLANG[code];
  if (explicit) {
    const known = new Set(countriesForLocale(code).map((c) => c.iso));
    return explicit
      .filter((iso) => known.has(iso))
      .map((iso) => `${lang}-${iso.toUpperCase()}`);
  }


  return countriesForLocale(code)
    .filter((c) => {
      const owner = DOMINANT_BY_COUNTRY[c.iso];
      return !owner || owner === code;
    })
    .slice(0, MAX_REGION_TAGS)
    .map((c) => `${lang}-${c.iso.toUpperCase()}`);
}



/** Valid Facebook `og:locale:alternate` values for a locale's target countries. */
export function ogLocaleAlternates(code: string): string[] {
  return countriesForLocale(code)
    .map((c) => c.fbLocale)
    .filter((v): v is string => !!v)
    .slice(0, MAX_REGION_TAGS);
}


/**
 * Ordered list of country names for description/keyword enrichment.
 * Deduplicated, capped by `limit` (default 10 for keywords, 3 for descriptions).
 */
export function countryNames(code: string, limit?: number): string[] {
  const names = countriesForLocale(code).map((c) => c.name);
  return typeof limit === "number" ? names.slice(0, limit) : names;
}
